/**
 * Повтор розыгрыша: график, состояние участников и хронология восстанавливаются на любой момент
 * записи. Можно идти по шагам, перематывать ползунком или включить воспроизведение.
 * Участник видит историю своего прогноза, администратор — прогнозы всех игроков.
 */
import { useEffect, useMemo, useState } from 'react';
import type { ChartPayload, Direction, GameReplay, ReplayEvent } from '@mt/protocol';
import { api, useLoad } from '../../lib/api';
import { clock, cr, crn, pts } from '../../lib/format';
import { Link } from '../../lib/router';
import { downloadPackage, fetchPackage } from '../../lib/verification';
import { client, useApp } from '../../net/client';
import type { SignedPackage } from '@mt/verification';
import { Chart } from '../Chart';
import { VerifyDialog } from '../VerifyDialog';
import { Avatar, dateTime, signedCr, toneOf } from '../kit';

const ACTION: Record<string, string> = { ante: 'взнос', fold: 'пас', check: 'чек', call: 'колл', bet: 'ставка', raise: 'райз', allin: 'олл-ин' };

interface SeatState {
  committed: number;
  roundBet: number;
  folded: boolean;
  last: string | null;
}

/** Состояние участников после первых n событий хронологии. */
function seatsAt(r: GameReplay, n: number): Map<string, SeatState> {
  const m = new Map(r.seats.map((s) => [s.playerId, { committed: 0, roundBet: 0, folded: false, last: null as string | null }]));
  let round = 0;
  for (const e of r.events.slice(0, n)) {
    if (e.round !== round && e.kind !== 'roundClosed') {
      round = e.round;
      for (const s of m.values()) s.roundBet = 0;
    }
    const s = e.playerId ? m.get(e.playerId) : undefined;
    if (!s) continue;
    if (e.kind === 'action') {
      const a = e.action ?? '';
      if (a === 'ante' || a === 'call' || a === 'allin') {
        s.committed += e.amount ?? 0;
        if (a !== 'ante') s.roundBet += e.amount ?? 0;
      } else if (a === 'bet' || a === 'raise') {
        // для ставки и райза записана итоговая ставка в круге — доплата = разница
        const pay = (e.amount ?? 0) - s.roundBet;
        s.committed += pay;
        s.roundBet = e.amount ?? 0;
      } else if (a === 'fold') s.folded = true;
      s.last = `${ACTION[a] ?? a}${e.amount && a !== 'fold' && a !== 'check' ? ' ' + crn(e.amount) : ''}${e.auto ? ' · авто' : ''}`;
    } else if (e.kind === 'uncalledReturn') {
      s.committed -= e.amount ?? 0;
      s.roundBet -= e.amount ?? 0;
      s.last = `возврат ${crn(e.amount ?? 0)}`;
    } else if (e.kind === 'autoFold') {
      s.folded = true;
    }
  }
  return m;
}

function forecastAt(r: GameReplay, playerId: string, at: number): { direction: Direction | null; points: number | null } {
  const list = r.forecasts[playerId] ?? [];
  let cur: { direction: Direction | null; points: number | null } = { direction: null, points: null };
  for (const f of list) if (f.at <= at) cur = { direction: f.direction, points: f.points };
  return cur;
}

export function ReplayPage({ gameId, admin }: { gameId: string; admin: boolean }) {
  const { data: r, error } = useLoad(() => api.replay(gameId), [gameId]);
  if (error) return <div className="page"><p className="notice danger">{error}</p></div>;
  if (!r) return <div className="page center dim">Загрузка записи розыгрыша…</div>;
  return <Replay r={r} admin={admin} />;
}

function Replay({ r, admin }: { r: GameReplay; admin: boolean }) {
  const me = useApp((s) => s.profile?.id ?? null);
  const total = r.events.length;
  const [pos, setPos] = useState(total);
  const [playing, setPlaying] = useState(false);
  const [verify, setVerify] = useState<SignedPackage | undefined>(undefined);
  const atEnd = pos >= total;

  useEffect(() => {
    if (!playing) return;
    if (atEnd) return setPlaying(false);
    const id = setTimeout(() => setPos((p) => Math.min(total, p + 1)), 650);
    return () => clearTimeout(id);
  }, [playing, pos, atEnd, total]);

  const cursorAt = atEnd ? r.settlement.settledAt : (r.events[Math.max(0, pos - 1)]?.at ?? r.startAt);
  const target = r.startAt + 3 * r.minuteMs;
  const chart: ChartPayload = useMemo(
    () => ({
      gameId: r.gameId,
      units: 'points',
      p0: r.p0,
      p0Points: r.p0,
      tick: r.tick,
      bars: r.bars.filter((b) => b.t < r.startAt || (b.t < target && b.t + r.minuteMs <= cursorAt) || (atEnd && b.t <= target)),
    }),
    [r, cursorAt, atEnd, target],
  );
  const seats = useMemo(() => seatsAt(r, pos), [r, pos]);
  const viewer = admin ? null : me;
  const myForecast = viewer ? forecastAt(r, viewer, cursorAt) : { direction: null, points: null };
  const stage = [...r.events.slice(0, pos)].reverse().find((e) => e.kind === 'stage' || e.kind === 'reveal');
  const compositionOpen = atEnd || r.events.slice(0, pos).some((e) => e.kind === 'reveal' && e.round === 3);
  const s = r.settlement;
  const participant = !admin && !!me && r.seats.some((x) => x.playerId === me);
  const run = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
    } catch (e) {
      client.toast('error', (e as Error).message);
    }
  };

  return (
    <div className="page replay-page">
      <nav className="crumbs">
        {admin ? <Link to="/admin?tab=games">Розыгрыши площадки</Link> : <Link to="/profile">Профиль</Link>} <span>/</span> <b>Розыгрыш {r.gameId}</b>
      </nav>
      <header className="replay-head panel">
        <div>
          <h1>
            Стол {r.ante / 100} кр. · {dateTime(s.settledAt)}
          </h1>
          <p className="muted-line">
            {r.mode === 'replay' ? 'воспроизведение истории' : 'реальное время'} · {r.source === 'alpaca' ? 'Alpaca' : 'синтетические данные'} · {r.seats.length} участников
          </p>
        </div>
        <dl className="head-stats">
          <div>
            <dt>Итог</dt>
            <dd>{s.status === 'cancelled' ? 'отменён' : s.kind === 'walkover' ? 'остался один' : 'расчёт'}</dd>
          </div>
          <div>
            <dt>Δ</dt>
            <dd className={`mono ${s.delta ? toneOf(s.delta) : ''}`}>{pts(s.delta, true)}</dd>
          </div>
          <div>
            <dt>Банк</dt>
            <dd className="mono">{cr(s.players.reduce((a, p) => a + p.contributed, 0))}</dd>
          </div>
        </dl>
      </header>

      {r.legacy ? (
        <p className="notice warn">Запись перенесена из журнала прежней версии: хронология ходов и график не сохранялись, доступен только расчёт.</p>
      ) : (
        <div className="replay-grid">
          <div className="replay-main">
            <div className="replay-chart">
              <Chart chart={chart} startAt={r.startAt} minuteMs={r.minuteMs} forecast={myForecast} settled={atEnd} />
            </div>
            <div className="panel player-bar">
              <button className="btn tiny" onClick={() => setPos(0)} disabled={pos === 0} aria-label="В начало">
                ⏮
              </button>
              <button className="btn tiny" onClick={() => setPos((p) => Math.max(0, p - 1))} disabled={pos === 0} aria-label="Шаг назад">
                ◀
              </button>
              <button
                className="btn tiny primary"
                onClick={() => {
                  if (atEnd) setPos(0);
                  setPlaying((v) => !v);
                }}
              >
                {playing ? 'Пауза' : 'Воспроизвести'}
              </button>
              <button className="btn tiny" onClick={() => setPos((p) => Math.min(total, p + 1))} disabled={atEnd} aria-label="Шаг вперёд">
                ▶
              </button>
              <button className="btn tiny" onClick={() => setPos(total)} disabled={atEnd} aria-label="В конец">
                ⏭
              </button>
              <input type="range" min={0} max={total} value={pos} onChange={(e) => (setPlaying(false), setPos(Number(e.target.value)))} aria-label="Момент записи" />
              <span className="mono small dim">
                {pos}/{total} · {clock(cursorAt)}
              </span>
            </div>
            <p className="replay-stage">{atEnd ? 'Расчёт выполнен' : (stage?.text ?? 'Подготовка: взносы вносятся в банк')}</p>
          </div>

          <aside className="replay-side">
            <section className="panel">
              <h3 className="panel-title">Участники</h3>
              <ul className="replay-seats">
                {r.seats.map((x) => {
                  const st = seats.get(x.playerId)!;
                  const f = admin || x.playerId === me ? forecastAt(r, x.playerId, cursorAt) : null;
                  const res = atEnd ? s.players.find((p) => p.playerId === x.playerId) : undefined;
                  return (
                    <li key={x.playerId} className={`${st.folded ? 'folded' : ''} ${x.playerId === me ? 'me' : ''}`}>
                      <Avatar id={x.playerId} name={x.name} size={28} bot={x.isBot} />
                      <span className="rs-name">
                        <b>
                          {admin ? <Link to={`/admin/users/${x.playerId}`}>{x.name}</Link> : x.name}
                          {x.playerId === me && <em className="tag me">вы</em>}
                        </b>
                        <small>{st.folded ? 'пас' : (st.last ?? '—')}</small>
                      </span>
                      <span className="rs-forecast">
                        {f?.direction ? (
                          <span className={f.direction === 'up' ? 'up' : 'down'}>
                            {f.direction === 'up' ? '▲' : '▼'} {pts(f.points)}
                          </span>
                        ) : f ? (
                          <span className="dim">—</span>
                        ) : null}
                      </span>
                      <span className="rs-money mono">{res ? <b className={toneOf(res.net)}>{signedCr(res.net)}</b> : crn(st.committed)}</span>
                    </li>
                  );
                })}
              </ul>
              <p className="muted-line small">{atEnd ? 'Справа — чистый результат.' : 'Справа — внесено в банк к этому моменту.'} {admin ? 'Прогнозы видны администратору.' : 'Чужие прогнозы видны в расчёте.'}</p>
            </section>
            <section className="panel">
              <h3 className="panel-title">Состав</h3>
              {compositionOpen ? (
                <div className="composition">
                  {s.composition.map((c) => (
                    <span key={c.symbol} className="ticker">
                      <b>{c.symbol}</b> {c.name}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="dim">раскрывается в круге 3</p>
              )}
            </section>
          </aside>
        </div>
      )}

      <div className="replay-bottom">
        {!r.legacy && (
          <section className="panel">
            <h3 className="panel-title">Хронология</h3>
            <ol className="timeline">
              {r.events.map((e, i) => (
                <TimelineItem key={e.seq} e={e} active={i === pos - 1} future={i >= pos} onClick={() => (setPlaying(false), setPos(i + 1))} />
              ))}
            </ol>
          </section>
        )}
        <section className="panel">
          <h3 className="panel-title">Расчёт</h3>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Игрок</th>
                  <th>Прогноз</th>
                  <th className="num">Ошибка</th>
                  <th className="num">Внесено</th>
                  <th className="num">Выплата</th>
                  <th className="num">Итог</th>
                </tr>
              </thead>
              <tbody>
                {s.players.map((p) => (
                  <tr key={p.playerId} className={p.playerId === me ? 'me' : ''}>
                    <td data-label="Игрок">{p.name}</td>
                    <td data-label="Прогноз">{p.folded ? <span className="dim">пас</span> : p.direction ? <span className={p.direction === 'up' ? 'up' : 'down'}>{p.direction === 'up' ? '▲' : '▼'} {pts(p.points)}</span> : '—'}</td>
                    <td data-label="Ошибка" className="num mono">
                      {p.error === null ? '—' : pts(p.error)}
                    </td>
                    <td data-label="Внесено" className="num mono">
                      {crn(p.contributed)}
                    </td>
                    <td data-label="Выплата" className="num mono">
                      {crn(p.payout + p.refund)}
                    </td>
                    <td data-label="Итог" className={`num mono ${toneOf(p.net)}`}>
                      {signedCr(p.net)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="muted-line small">
            P₀ {pts(s.p0)} → P₁ {pts(s.p1)}; комиссия {cr(s.commissionTotal)}.{s.reason ? ` ${s.reason}.` : ''}
          </p>
          {participant && !r.legacy && (
            <div className="row-actions">
              <button className="btn tiny" onClick={() => void run(async () => setVerify((await fetchPackage(r.gameId)).signed))}>
                Проверить в браузере
              </button>
              <button className="btn tiny" onClick={() => void run(() => downloadPackage(r.gameId))}>
                Скачать пакет проверки
              </button>
            </div>
          )}
        </section>
      </div>
      {verify && <VerifyDialog initial={verify} onClose={() => setVerify(undefined)} />}
    </div>
  );
}

function TimelineItem({ e, active, future, onClick }: { e: ReplayEvent; active: boolean; future: boolean; onClick: () => void }) {
  return (
    <li className={`tl-item ${e.kind} ${active ? 'active' : ''} ${future ? 'future' : ''}`}>
      <button type="button" onClick={onClick}>
        <span className="mono dim">{clock(e.at)}</span>
        <span className="tl-round">{e.round === 0 ? 'подготовка' : `круг ${e.round}`}</span>
        <span className="tl-text">{e.text}</span>
      </button>
    </li>
  );
}
